import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Http from "./Http";
import {
  READ_ONLY_MESSAGE,
  getUserType,
  isViewer,
  isWriteRoute,
} from "./Permission";

const HIDDEN_CLASS = "read-only-hidden";

// Buttons that save something. They are recognised by their icon or by their
// label, because the app has no shared "action button" component.
const ACTION_ICON =
  /\b(fa-trash|fa-trash-o|fa-pencil|fa-pencil-square|fa-edit|fa-plus|fa-plus-square|fa-save|fa-floppy|pi-trash|pi-pencil|pi-plus|ion-trash|ion-edit|ion-plus)\b/i;
const ACTION_TEXT =
  /^(save|submit|update|delete|remove|add|create|edit|new|approve|reject|recommend|confirm|process|post|pay|receive|assign|import|sync)\b/i;

const isExempt = (el) => el.closest("[data-readonly-allow]") !== null;

// A link is hidden only when it points at a create/edit screen, so view,
// report and list links are never touched.
const isWriteLink = (el) => {
  const href = el.getAttribute("href") || "";
  return href !== "" && href !== "#" && isWriteRoute(href);
};

const isWriteButton = (el) =>
  ACTION_ICON.test(el.innerHTML || "") ||
  ACTION_TEXT.test((el.textContent || "").trim());

// Hides the add/edit/delete controls of the page the viewer is looking at.
// Links are checked everywhere (header and sidebar included) because their
// target is known exactly; the button heuristics only run on the page body.
const sweep = () => {
  document.querySelectorAll("a[href]").forEach((el) => {
    if (!isExempt(el) && isWriteLink(el)) el.classList.add(HIDDEN_CLASS);
  });

  document
    .querySelectorAll("#erp-content button, #erp-content input[type='submit']")
    .forEach((el) => {
      if (!isExempt(el) && isWriteButton(el)) el.classList.add(HIDDEN_CLASS);
    });
};

const AccessDenied = () => (
  <div className="am-mainpanel">
    <div className="am-pagetitle">
      <h5 className="am-title">View only access</h5>
    </div>
    <div className="am-pagebody">
      <div className="card pd-20 pd-sm-40 text-center">
        <p className="mg-b-20">{READ_ONLY_MESSAGE}</p>
        <div>
          <Link to="/dashboard" className="btn btn-primary" data-readonly-allow>
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Wraps the whole route tree. For an admin_viewer it blocks every create/edit
// screen and hides the action controls; for anybody else it renders as if it
// were not there.
const ReadOnlyGuard = ({ children }) => {
  const location = useLocation();
  const [userType, setUserTypeState] = useState(getUserType());

  // The user type is normally cached by the Http response interceptor when the
  // sidebar loads. Fetch it once when it is not known yet (first login).
  useEffect(() => {
    if (userType) return;
    const token = localStorage.getItem("token");
    if (!token || token === "null") return;

    let cancelled = false;
    Http.get("/staff-type-check")
      .then(() => {
        if (!cancelled) setUserTypeState(getUserType());
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [userType, location.pathname]);

  const viewer = isViewer();

  useEffect(() => {
    document.body.classList.toggle("read-only-mode", viewer);
  }, [viewer]);

  useEffect(() => {
    if (!viewer) return;

    sweep();
    let frame = null;
    const observer = new MutationObserver(() => {
      if (frame) return;
      frame = window.setTimeout(() => {
        frame = null;
        sweep();
      }, 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame) window.clearTimeout(frame);
    };
  }, [viewer, location.pathname]);

  if (viewer && isWriteRoute(location.pathname)) {
    return <AccessDenied />;
  }
  return children;
};
export default ReadOnlyGuard;